import React from 'react';
import { Button } from '../ui/button';
import { VideoCamera, CaretRight } from '@phosphor-icons/react';

export const LiveSessionsBookings = ({ classes, expandedClassId, participantsByClass, participantLoading, onToggleExpand, onToggleBlock, navigate }) => {
  if (!classes || classes.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-slate-100" data-testid="no-live-sessions">
        <VideoCamera size={48} className="text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-sm mb-1 font-medium">No live sessions yet</p>
        <p className="text-slate-400 text-xs mb-4">Host your first Yoga / Wellness live session to see bookings here.</p>
        <Button onClick={() => navigate('/live-classes')} className="bg-pink-500 hover:bg-pink-600 rounded-xl text-sm" data-testid="go-live-classes-btn">
          <VideoCamera size={14} className="mr-1.5" weight="fill" /> Go to Live Sessions
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="live-sessions-list">
      {classes.map(cls => {
        const expanded = expandedClassId === cls.class_id;
        const participants = participantsByClass[cls.class_id] || [];
        const blockedCount = participants.filter(p => p.blocked).length;
        return (
          <div key={cls.class_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`live-session-${cls.class_id}`}>
            <button onClick={() => onToggleExpand(cls.class_id)}
              className="w-full flex items-start justify-between p-4 hover:bg-slate-50 transition-colors text-left"
              data-testid={`expand-session-${cls.class_id}`}>
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center shrink-0">
                  <VideoCamera size={18} className="text-pink-500" weight="fill" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-slate-900 text-sm truncate">{cls.title}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">{cls.session_type} | {cls.date} | {cls.start_time} - {cls.end_time}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className="text-[11px] font-semibold text-pink-600">{cls.booked_count}/{cls.max_participants} booked</span>
                    {blockedCount > 0 && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{blockedCount} blocked</span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls.status === 'live' ? 'bg-red-100 text-red-700' : cls.status === 'completed' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
                      {(cls.status || 'upcoming').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              <CaretRight size={18} className={`text-slate-400 mt-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>

            {expanded && (
              <div className="border-t border-slate-100 bg-slate-50/40 px-4 py-3" data-testid={`session-participants-${cls.class_id}`}>
                {participantLoading === cls.class_id ? (
                  <p className="text-xs text-slate-400 py-3 text-center">Loading participants...</p>
                ) : participants.length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center">No participants booked yet for this session</p>
                ) : (
                  <div className="overflow-x-auto -mx-4 px-4">
                    <table className="w-full">
                      <thead>
                        <tr className="text-[10px] text-slate-400 uppercase tracking-wider">
                          <th className="text-left py-2 px-2 font-semibold">Buyer</th>
                          <th className="text-left py-2 px-2 font-semibold">Email</th>
                          <th className="text-left py-2 px-2 font-semibold">Phone</th>
                          <th className="text-left py-2 px-2 font-semibold">Location</th>
                          <th className="text-left py-2 px-2 font-semibold">Status</th>
                          <th className="text-right py-2 px-2 font-semibold">Action</th>
                        </tr>
                      </thead>
                      <tbody className="text-xs">
                        {participants.map(p => (
                          <tr key={p.user_id} className="border-t border-slate-100" data-testid={`participant-row-${p.user_id}`}>
                            <td className="py-2.5 px-2 text-slate-800 font-medium">{p.name || '—'}</td>
                            <td className="py-2.5 px-2 text-slate-600 truncate max-w-[180px]" title={p.email}>{p.email || '—'}</td>
                            <td className="py-2.5 px-2 text-slate-600">{p.phone || '—'}</td>
                            <td className="py-2.5 px-2 text-slate-600">{p.city || '—'}</td>
                            <td className="py-2.5 px-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${p.blocked ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid={`participant-status-${p.user_id}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${p.blocked ? 'bg-red-500' : 'bg-emerald-500'}`} />
                                {p.blocked ? 'Blocked' : 'Active'}
                              </span>
                            </td>
                            <td className="py-2.5 px-2 text-right">
                              <Button size="sm" variant="outline"
                                onClick={() => onToggleBlock(cls.class_id, p.user_id, p.blocked)}
                                className={`h-7 text-[11px] rounded-lg ${p.blocked ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                                data-testid={`toggle-block-${p.user_id}`}>
                                {p.blocked ? 'Activate' : 'Block'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
